import React, { useState, useEffect, useRef } from 'react'
import {
  CButton,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilLockUnlocked } from '@coreui/icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeLowVision } from "@fortawesome/free-solid-svg-icons";
import GuestLayout from '@/layout/GuestLayout'
import { useFormik } from 'formik'
import validationHelper from "@/helpers/validationHelper";
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { loggedUser } from '@/store/features/userSlice'
import { useNavigate } from "react-router-dom";
import { UserService } from '@/api'
import { dashboard, signIn } from '@/libs/routes'
import { useTranslationContext } from '@/context/TranslationContext'
import { reset_password } from '@/constants/translation/auth.translation'

/**
 * @name ResetPassword
 *
 * @description
 * The ResetPassword component allows users to reset their password using the resetToken.
 * It takes the new password and confirms it, then sends it to the backend with the resetToken.
 * If successful, it redirects to the sign-in page.
 * 
 * API Response Format:
 * {
 *   message: "Password reset successfully",
 *   data: {}
 * }
 *
 * @returns {JSX.Element} The JSX representation of the Reset Password page.
 */
const ResetPassword = () => {
  // Translation context to retrieve translated strings.
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [apiError, setApiError] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const submissionBlockRef = useRef(false);

  // Retrieve logged-in user information from the Redux store.
  const checkLoggenInUser = useSelector(loggedUser);

  // Validation schema for the Reset Password form using Formik.
  const validationSchema = validationHelper.resetPasswordValidation();

  // Formik hook for handling form state and submission.
  const resetPasswordForm = useFormik({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Block any additional submissions immediately
      if (submissionBlockRef.current) {
        return;
      }
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      doResetPassword(values);
    },
  });

  // Load stored resetToken from localStorage on component mount
  useEffect(() => {
    const storedResetToken = localStorage.getItem('forgot_password_reset_token');
    
    if (!storedResetToken) {
      toast.error(t(reset_password.session_expired_error));
      navigate('/forgot-password');
      return;
    }
    
    setResetToken(storedResetToken);
  }, [navigate, t]);

  // Redirects logged-in users to the dashboard to prevent access to the Reset Password page.
  useEffect(() => {
    if (checkLoggenInUser && checkLoggenInUser.isLoggedIn) {
      navigate(dashboard);
    }
  }, [checkLoggenInUser, navigate]);

  // Toggle password visibility
  const toggleNewPassword = () => setShowNewPassword(!showNewPassword);
  const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  /**
   * The API call for resetting the password.
   * Sends the new password and resetToken to reset the password.
   * If successful, clears stored tokens and redirects to sign-in page.
   */
  const doResetPassword = async (values) => {
    try {
      const response = await UserService.resetPassword({
        resetToken: resetToken,
        new_password: values.new_password
      });

      // Clear all stored tokens from localStorage
      localStorage.removeItem('forgot_password_encoded_token');
      localStorage.removeItem('forgot_password_reset_token');
      localStorage.removeItem('forgot_password_email');
      localStorage.removeItem('forgot_password_otp');

      toast.success(response.message || t(reset_password.password_reset_success));
      navigate(signIn);
    } catch (error) {
      
      const message = error?.response?.data?.message || error?.message || t(reset_password.failed_to_reset_error);
      
      // Set API error to override validation errors
      setApiError(message);
      toast.error(message);
      
      // Clear both password fields on API failure
      resetPasswordForm.setFieldValue('new_password', '');
      resetPasswordForm.setFieldValue('confirm_password', '');
      
    } finally {
      setIsSubmitting(false);
      submissionBlockRef.current = false;
    }
  };

  // Custom form submit handler with aggressive blocking
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clear API error when user tries again
    if (apiError) {
      setApiError(null);
    }
    
    // Block any additional submissions immediately
    if (submissionBlockRef.current) {
      return;
    }
    
    // Validate and submit
    if (resetPasswordForm.isValid) {
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      doResetPassword(resetPasswordForm.values);
    } else {
      // Trigger form validation
      resetPasswordForm.handleSubmit(e);
    }
  };

  const redirectToLogin = () => {
    navigate(signIn);
  };

  return (
    <>
      <CForm className={`mt-4 ${isSubmitting ? 'pe-none opacity-50' : ''}`} onSubmit={handleSubmit}>
        <h1>{t(reset_password.main_heading)}</h1>
        <p className="text-body-secondary">
          {t(reset_password.sub_heading)}
        </p>
        
        <div className="mb-3">
          <CInputGroup>
            <CInputGroupText onClick={toggleNewPassword} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={showNewPassword ? faEye : faEyeLowVision} />
            </CInputGroupText>
            <CFormInput
              placeholder={t(reset_password.new_password_placeholder)}
              autoComplete="new-password"
              type={showNewPassword ? "text" : "password"}
              name="new_password"
              className="form-control same-input-cls"
              onChange={resetPasswordForm.handleChange}
              onBlur={resetPasswordForm.handleBlur}
              value={resetPasswordForm.values.new_password}
              feedbackInvalid={apiError ? null : (resetPasswordForm.errors.new_password ? t(resetPasswordForm.errors.new_password) : null)} />
          </CInputGroup>
          {/* Display error message if new password is invalid */}
          {apiError ? (
            <div className="error text-danger mt-1">{apiError}</div>
          ) : resetPasswordForm.errors.new_password ? (
            <div className="error text-danger mt-1">{t(resetPasswordForm.errors.new_password)}</div>
          ) : null}
        </div>

        <div className="mb-3">
          <CInputGroup>
            <CInputGroupText onClick={toggleConfirmPassword} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeLowVision} />
            </CInputGroupText>
            <CFormInput
              placeholder={t(reset_password.confirm_password_placeholder)}
              autoComplete="new-password"
              type={showConfirmPassword ? "text" : "password"}
              name="confirm_password"
              className="form-control same-input-cls"
              onChange={resetPasswordForm.handleChange}
              onBlur={resetPasswordForm.handleBlur}
              value={resetPasswordForm.values.confirm_password}
              feedbackInvalid={apiError ? null : (resetPasswordForm.errors.confirm_password ? t(resetPasswordForm.errors.confirm_password) : null)} />
          </CInputGroup>
          {/* Display error message if confirm password is invalid */}
          {apiError ? (
            <div className="error text-danger mt-1">{apiError}</div>
          ) : resetPasswordForm.errors.confirm_password ? (
            <div className="error text-danger mt-1">{t(resetPasswordForm.errors.confirm_password)}</div>
          ) : null}
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="d-flex gap-2 mt-4">
          <CButton color="primary" className="px-4 flex-grow-1 d-flex gap-2 align-items-center justify-content-center" type="submit" disabled={isSubmitting}>
            {isSubmitting && <CSpinner size="sm" variant="grow" />}{t(reset_password.submit_btn_text)}
          </CButton>
          <CButton color="secondary" className="px-4 flex-grow-1" type="button" disabled={isSubmitting} onClick={redirectToLogin}>
            {t(reset_password.cancel_btn_text)}
          </CButton>
        </div>
      </CForm>
    </>
  );
};

/**
 * @name getLayout
 *
 * @description
 * This function is used to wrap the ResetPassword component with a `GuestLayout`. 
 * This layout is designed specifically for unauthenticated users and provides a consistent design for guest pages.
 * 
 * @param {JSX.Element} page - The current page (ResetPassword).
 * @returns {JSX.Element} The page wrapped in a `GuestLayout`.
 */
ResetPassword.getLayout = function getLayout(page) {
  return (
    <GuestLayout>{page}</GuestLayout>
  );
};

export default ResetPassword;
